/**
 * アサイン履歴画面の絞り込みと並び替え（純粋関数）。
 *
 * 履歴は年に数百件規模なので、API は全件を返し、絞り込み・並び替えは画面側で行う。
 * 文字列の列は部分一致（大文字小文字を区別しない）、アサイン日は期間で絞る。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business アサイン履歴を案件番号・案件名・アサイン日・役割ごとのメンバー・KPI のチームで絞り込み、任意の列で並び替える
 * @graph-connects none
 */

import {
  assignmentHistoryEntryMatchesTeam,
  type AssignmentHistoryEntry,
} from '@cortex/domain-project-intake-contract';
import { compareTableValues, type SortDir } from './table-sort.js';

/** 並び替えできる列。 @graph-connects none */
export type AssignmentHistorySortKey =
  | 'issueKey'
  | 'summary'
  | 'assignedDate'
  | 'requirementEngineer'
  | 'developer'
  | 'reviewer';

/** 部分一致で絞り込む文字列の列。 @graph-connects none */
export type AssignmentHistoryTextFilterKey = Exclude<AssignmentHistorySortKey, 'assignedDate'>;

/** 絞り込み条件。空文字はその条件を使わない。 @graph-connects none */
export interface AssignmentHistoryFilters {
  issueKey: string;
  summary: string;
  requirementEngineer: string;
  developer: string;
  reviewer: string;
  /** アサイン日の下限（YYYY-MM-DD、当日を含む）。 */
  assignedFrom: string;
  /** アサイン日の上限（YYYY-MM-DD、当日を含む）。 */
  assignedTo: string;
  /** KPI のチーム（`TEAM_OPTIONS` の value）。`'all'` は絞り込み無し。 */
  team: string;
}

/** 絞り込み無しの条件。 @graph-connects none */
export const EMPTY_ASSIGNMENT_HISTORY_FILTERS: AssignmentHistoryFilters = {
  issueKey: '',
  summary: '',
  requirementEngineer: '',
  developer: '',
  reviewer: '',
  assignedFrom: '',
  assignedTo: '',
  team: 'all',
};

/** 部分一致で絞り込む列（画面のフィルター行の並び）。 @graph-connects none */
export const ASSIGNMENT_HISTORY_TEXT_FILTER_KEYS: readonly AssignmentHistoryTextFilterKey[] = [
  'issueKey',
  'summary',
  'requirementEngineer',
  'developer',
  'reviewer',
];

/** 値が条件を部分一致で満たすか（条件が空なら常に満たす）。 @graph-connects none */
function matchesText(value: string | null, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  return (value ?? '').toLowerCase().includes(q);
}

/** 1 件が全条件を満たすか。 @graph-connects none */
function matches(
  entry: AssignmentHistoryEntry,
  filters: AssignmentHistoryFilters,
  memberTeams: Readonly<Record<string, readonly string[]>>,
): boolean {
  const textOk = ASSIGNMENT_HISTORY_TEXT_FILTER_KEYS.every((key) =>
    matchesText(entry[key], filters[key]),
  );
  if (!textOk) return false;
  if (filters.assignedFrom !== '' && entry.assignedDate < filters.assignedFrom) return false;
  if (filters.assignedTo !== '' && entry.assignedDate > filters.assignedTo) return false;
  if (
    filters.team !== 'all' &&
    !assignmentHistoryEntryMatchesTeam(entry, memberTeams, filters.team)
  ) {
    return false;
  }
  return true;
}

/**
 * 課題キーの番号部分（`AIRCLOSET-150123` → 150123）。番号順に並べるのに使う
 * （文字列比較だと桁の少ない番号が後ろに来る）。
 * @graph-connects none
 */
function issueNumber(issueKey: string): number | null {
  const match = /(\d+)$/.exec(issueKey);
  return match ? Number(match[1]) : null;
}

/** 並び替えに使う列の値。 @graph-connects none */
function sortValue(entry: AssignmentHistoryEntry, key: AssignmentHistorySortKey) {
  return key === 'issueKey' ? issueNumber(entry.issueKey) : entry[key];
}

/**
 * 絞り込んでから並び替える。同じ値の行はアサイン実行時刻の新しい順に並べる
 * （同じ日に複数のアサインがあっても順序が毎回変わらないように）。
 * @graph-connects none
 */
export function filterAndSortAssignmentHistory(
  items: readonly AssignmentHistoryEntry[],
  filters: AssignmentHistoryFilters,
  sort: { key: AssignmentHistorySortKey; dir: SortDir },
  memberTeams: Readonly<Record<string, readonly string[]>> = {},
): AssignmentHistoryEntry[] {
  return items
    .filter((entry) => matches(entry, filters, memberTeams))
    .sort(
      (a, b) =>
        compareTableValues(sortValue(a, sort.key), sortValue(b, sort.key), sort.dir) ||
        b.assignedAt.localeCompare(a.assignedAt),
    );
}
